import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import CoupangAd from '../components/CoupangAd';

function ParentDashboard() {
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    missionCount: 0,
    pendingApprovals: 0,
    pendingRewards: 0,
    weeklyCompletionRate: 0,
    estimatedPayment: 0
  });
  const [children, setChildren] = useState([]);
  const [penaltyAmount, setPenaltyAmount] = useState(1000);
  const [showPenaltyForm, setShowPenaltyForm] = useState(false);
  const [penaltyForm, setPenaltyForm] = useState({ childId: '', points: 1, reason: '' });
  const [penaltyLoading, setPenaltyLoading] = useState(false);
  const [penaltyResult, setPenaltyResult] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    // 로그인 상태 확인
    const token = localStorage.getItem('token');
    const userData = localStorage.getItem('user');

    if (!token || !userData) {
      // 로그인되지 않은 경우 로그인 페이지로 리다이렉트
      navigate('/parent/login');
      return;
    }

    try {
      const parsedUser = JSON.parse(userData);
      if (parsedUser.type !== 'parent') {
        // 부모가 아닌 경우 홈으로 리다이렉트
        navigate('/');
        return;
      }
      setUser(parsedUser);

      // 대시보드 통계 데이터 로드
      fetchDashboardStats();
      fetchChildrenAndSettings();
    } catch (error) {
      console.error('사용자 데이터 파싱 오류:', error);
      navigate('/parent/login');
    }
  }, [navigate]);

  // 화면 복귀 시 자동 새로고침 + 30초 폴링
  useEffect(() => {
    const refresh = () => {
      fetchDashboardStats();
      fetchChildrenAndSettings();
    };

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') refresh();
    };

    document.addEventListener('visibilitychange', handleVisibility);
    const interval = setInterval(refresh, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility);
      clearInterval(interval);
    };
  }, []);

  const fetchChildrenAndSettings = async () => {
    try {
      const [childrenRes, settingsRes] = await Promise.all([
        api.get('/families/children'),
        api.get('/families/me/settings')
      ]);
      setChildren(childrenRes.data.children || []);
      setPenaltyAmount(settingsRes.data?.penalty_amount || 1000);
      setPenaltyForm(prev => ({
        ...prev,
        childId: childrenRes.data.children?.[0]?.id || ''
      }));
    } catch (error) {
      console.error('Children/settings fetch error:', error);
    }
  };

  const handlePenaltySubmit = async () => {
    if (!penaltyForm.childId || !penaltyForm.reason.trim()) return;
    setPenaltyLoading(true);
    setPenaltyResult(null);
    try {
      const totalAmount = penaltyForm.points * penaltyAmount;
      await api.post(`/wallets/${penaltyForm.childId}/penalty`, {
        amount: totalAmount,
        reason: penaltyForm.reason
      });
      const child = children.find(c => c.id === penaltyForm.childId);
      setPenaltyResult({ success: true, msg: `${child?.name}에게 ${totalAmount.toLocaleString()}원 벌점이 부과되었습니다.` });
      setPenaltyForm(prev => ({ ...prev, points: 1, reason: '' }));
    } catch (error) {
      setPenaltyResult({ success: false, msg: error.response?.data?.error || '벌점 부과에 실패했습니다.' });
    } finally {
      setPenaltyLoading(false);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      // 미션 개수 조회
      const missionsResponse = await api.get('/missions');

      // 승인 대기 목록 조회
      const pendingResponse = await api.get('/assignments/pending-approvals');

      // 주간 정산 미리보기 조회
      const settlementResponse = await api.get('/settlements/preview');

      // 선물 요청 대기 목록 조회
      const rewardsResponse = await api.get('/rewards/family-requests');

      setStats(prev => ({
        ...prev,
        missionCount: missionsResponse.data.missions?.length || 0,
        pendingApprovals: pendingResponse.data.assignments?.length || 0,
        pendingRewards: rewardsResponse.data.requests?.filter(r => r.status === 'pending').length || 0,
        weeklyCompletionRate: settlementResponse.data.summary?.averageCompletionRate || 0,
        estimatedPayment: settlementResponse.data.summary?.totalFamilyAmount || 0
      }));

    } catch (error) {
      console.error('Dashboard stats fetch error:', error);
      // 에러가 발생해도 기본값으로 설정
      setStats(prev => ({
        ...prev,
        missionCount: 0,
        pendingApprovals: 0,
        pendingRewards: 0,
        weeklyCompletionRate: 0,
        estimatedPayment: 0
      }));
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/');
  };

  if (!user) {
    return (
      <div className="card">
        <div style={{ textAlign: 'center', padding: '20px' }}>
          로딩 중...
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-container">
      {/* 헤더 */}
      <div className="dashboard-header">
        <div className="header-content">
          <h1 className="dashboard-title">🍃 도토리 저금통</h1>
          <div className="header-user">
            <span>안녕하세요, {user.name}님!</span>
            <button
              onClick={handleLogout}
              className="btn btn-outline"
              style={{ marginLeft: '10px', fontSize: '0.9rem' }}
            >
              로그아웃
            </button>
          </div>
        </div>
      </div>

      {/* 메인 컨텐츠 */}
      <div className="dashboard-content">
        <div className="welcome-section">
          <h2>👨‍👩‍👧‍👦 {user.familyName} 관리</h2>
          <p>가족의 미션과 용돈을 관리해보세요</p>
        </div>

        {/* 빠른 액션 */}
        <div className="quick-actions">
          <h3>빠른 작업</h3>
          <div className="action-buttons">
            <button className="btn btn-outline" onClick={() => navigate('/parent/missions/new')}>
              📝 새 미션 추가
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/rewards')}>
              🎁 선물 요청
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/children')}>
              👶 자녀 관리
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/settings')}>
              ⚙️ 가족 설정
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/reports')}>
              📈 월간 리포트
            </button>
            <button className="btn btn-outline" onClick={() => navigate('/parent/wallets')}>
              💳 지갑 관리
            </button>
          </div>
        </div>

        {/* 쿠팡 파트너스 다이나믹 배너 (iframe 버전) */}
        <CoupangAd type="iframe" category="kids" />

        {/* 대시보드 카드들 */}
        <div className="dashboard-grid">
          <div className="dashboard-card">
            <div className="card-icon">✅</div>
            <h3>승인 대기</h3>
            <p className="card-number">{stats.pendingApprovals}건</p>
            <p className="card-description">자녀들의 미션 체크를 확인해주세요</p>
            <Link to="/parent/approvals" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              승인하기
            </Link>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">🎁</div>
            <h3>선물 요청</h3>
            <p className="card-number">{stats.pendingRewards}건</p>
            <p className="card-description">자녀들의 선물 요청을 확인해주세요</p>
            <Link to="/parent/rewards" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              선물 관리
            </Link>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">🎯</div>
            <h3>미션 관리</h3>
            <p className="card-number">{stats.missionCount}개</p>
            <p className="card-description">새로운 미션을 추가하거나 수정하세요</p>
            <Link to="/parent/missions" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              미션 관리
            </Link>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">💰</div>
            <h3>이번 주 정산</h3>
            <p className="card-number">{stats.estimatedPayment.toLocaleString()}원</p>
            <p className="card-description">이번 주 예상 지급액을 확인하세요</p>
            <button className="btn btn-primary btn-full" onClick={() => navigate('/parent/settlements')}>
              정산하기
            </button>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">📊</div>
            <h3>완료율</h3>
            <p className="card-number">{stats.weeklyCompletionRate}%</p>
            <p className="card-description">이번 주 가족 미션 완료율</p>
            <Link to="/parent/reports" className="btn btn-primary btn-full" style={{ textDecoration: 'none', display: 'block', textAlign: 'center' }}>
              리포트 보기
            </Link>
          </div>

          <div className="dashboard-card">
            <div className="card-icon">🚫</div>
            <h3>벌점 부과</h3>
            <p className="card-description">1점당 {penaltyAmount.toLocaleString()}원 차감</p>
            {!showPenaltyForm ? (
              <button className="btn btn-primary btn-full" onClick={() => { setShowPenaltyForm(true); setPenaltyResult(null); }}>
                벌점 부과하기
              </button>
            ) : (
              <div className="penalty-form">
                <select
                  value={penaltyForm.childId}
                  onChange={e => setPenaltyForm(prev => ({ ...prev, childId: e.target.value }))}
                  className="penalty-select"
                >
                  {children.map(c => (
                    <option key={c.id} value={c.id}>{c.name}</option>
                  ))}
                </select>
                <div className="penalty-points-row">
                  <input
                    type="number"
                    min="1"
                    max="10"
                    value={penaltyForm.points}
                    onChange={e => setPenaltyForm(prev => ({ ...prev, points: parseInt(e.target.value) || 1 }))}
                    className="penalty-points-input"
                  />
                  <span className="penalty-points-label">점 = {(penaltyForm.points * penaltyAmount).toLocaleString()}원 차감</span>
                </div>
                <input
                  type="text"
                  placeholder="벌점 사유 입력"
                  value={penaltyForm.reason}
                  onChange={e => setPenaltyForm(prev => ({ ...prev, reason: e.target.value }))}
                  className="penalty-reason-input"
                />
                {penaltyResult && (
                  <p style={{ color: penaltyResult.success ? '#27ae60' : '#e74c3c', fontSize: '0.85rem', margin: '6px 0' }}>
                    {penaltyResult.msg}
                  </p>
                )}
                <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
                  <button
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                    onClick={handlePenaltySubmit}
                    disabled={penaltyLoading || !penaltyForm.reason.trim()}
                  >
                    {penaltyLoading ? '처리 중...' : '부과'}
                  </button>
                  <button
                    className="btn btn-outline"
                    style={{ flex: 1 }}
                    onClick={() => { setShowPenaltyForm(false); setPenaltyResult(null); }}
                  >
                    취소
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <style>{`
        .dashboard-container {
          min-height: 100vh;
          background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
        }

        .dashboard-header {
          background: white;
          border-bottom: 1px solid #e0e0e0;
          padding: 15px 0;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .header-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .dashboard-title {
          margin: 0;
          color: #8B4513;
          font-size: 1.5rem;
        }

        .header-user {
          display: flex;
          align-items: center;
          font-weight: 500;
        }

        .dashboard-content {
          max-width: 1200px;
          margin: 0 auto;
          padding: 30px 20px;
        }

        .welcome-section {
          text-align: center;
          margin-bottom: 40px;
        }

        .welcome-section h2 {
          color: #333;
          margin-bottom: 10px;
        }

        .welcome-section p {
          color: #666;
          font-size: 1.1rem;
        }

        .dashboard-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 25px;
          margin-top: 40px;
        }

        .dashboard-card {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          text-align: center;
          transition: transform 0.2s, box-shadow 0.2s;
        }

        .dashboard-card:hover {
          transform: translateY(-5px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .card-icon {
          font-size: 3rem;
          margin-bottom: 15px;
        }

        .dashboard-card h3 {
          color: #333;
          margin-bottom: 10px;
          font-size: 1.2rem;
        }

        .card-number {
          font-size: 2rem;
          font-weight: bold;
          color: #8B4513;
          margin: 10px 0;
        }

        .card-description {
          color: #666;
          font-size: 0.9rem;
          margin-bottom: 20px;
          line-height: 1.4;
        }

        .btn-full {
          width: 100%;
        }

        .quick-actions {
          background: white;
          padding: 30px;
          border-radius: 15px;
          box-shadow: 0 4px 6px rgba(0,0,0,0.1);
          margin-bottom: 40px;
        }

        .quick-actions h3 {
          color: #333;
          margin-bottom: 20px;
          text-align: center;
        }

        .action-buttons {
          display: flex;
          flex-wrap: wrap;
          gap: 15px;
        }

        .action-buttons .btn {
          flex: 1 1 200px;
          min-width: 200px;
        }

        @media (max-width: 768px) {
          .header-content {
            flex-direction: column;
            gap: 15px;
          }

          .dashboard-grid {
            grid-template-columns: 1fr;
          }

          .action-buttons .btn {
            flex: 1 1 100%;
            min-width: auto;
          }
        }

        .penalty-form {
          display: flex;
          flex-direction: column;
          gap: 8px;
          margin-top: 8px;
          text-align: left;
        }

        .penalty-select,
        .penalty-reason-input {
          width: 100%;
          padding: 8px 10px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.95rem;
          box-sizing: border-box;
        }

        .penalty-points-row {
          display: flex;
          align-items: center;
          gap: 8px;
        }

        .penalty-points-input {
          width: 60px;
          padding: 8px;
          border: 1px solid #ddd;
          border-radius: 8px;
          font-size: 0.95rem;
          text-align: center;
        }

        .penalty-points-label {
          font-size: 0.9rem;
          color: #e74c3c;
          font-weight: 600;
        }
      `}</style>
    </div>
  );
}

export default ParentDashboard;